// src/services/payroll/lohnjournalSpiegelService.ts

/**
 * Service für den Zugriff auf DATEV-Lohnjournale (LOJO) im Dokumentenspiegel.
 *
 * Hintergrund: Für DATEV-Kunden spiegelt der Sync das Lohnjournal automatisch
 * nach Supabase (Tabelle `datev_documents`, Bucket `datev-documents`). Dieser
 * Service macht das gespiegelte Journal für den Rechnungsentwurf verfügbar,
 * damit die Arbeitnehmeranzahl übernommen werden kann, ohne dass jemand die
 * Datei erneut aus DATEV exportiert und hochlädt.
 *
 * Wichtig zur Datenhaltung in `datev_documents`:
 * - Ein Unique-Index auf (customer_id, period, document_type, employee_number)
 *   garantiert genau eine Zeile pro Kunde, Monat und Dokumententyp.
 * - `replaced_at` bedeutet NICHT, dass die Zeile veraltet ist. Die Spalte
 *   markiert, dass das Dokument bereits einmal durch eine neuere Fassung aus
 *   DATEV ersetzt wurde. `storage_path` zeigt immer auf die aktuelle Fassung,
 *   die Vorgängerversion liegt unter `previous_storage_path` im Archiv.
 *   Es darf deshalb NICHT auf `replaced_at IS NULL` gefiltert werden.
 *
 * Wichtig zum Download:
 * - Die Storage-Objekte tragen `cache-control: max-age=3600`. Ein Download
 *   über `supabase.storage.download()` landet damit im HTTP-Cache des
 *   Browsers. Wird eine Datei innerhalb dieser Stunde in DATEV ersetzt und
 *   vom Sync neu geschrieben, würde ein erneuter Abruf weiterhin die alte
 *   Fassung liefern. Der Download läuft deshalb über einen frisch signierten
 *   Link und `cache: 'no-store'`. Es gilt ausnahmslos die aktuelle Fassung.
 */

import { supabase } from '../../lib/supabaseClient';

const BUCKET = 'datev-documents';
const DOKUMENTTYP = 'LOJO';

/** Gültigkeit des signierten Links in Sekunden. Nur für den direkten Abruf. */
const SIGNED_URL_GUELTIGKEIT_SEKUNDEN = 120;

/**
 * Ein im Spiegel gefundenes Lohnjournal auf Mandantenebene.
 */
export interface LohnjournalSpiegelTreffer {
  id: string;
  customerId: string;
  storagePath: string;
  /** Abrechnungsmonat als Datum des Monatsersten, Format YYYY-MM-DD */
  periode: string;
  /** Zeitstempel, den DATEV für das Dokument liefert */
  datevZeitstempel: string;
  /** Gesetzt, wenn DATEV das Journal bereits durch die vorliegende Fassung ersetzt hat */
  ersetztAm: string | null;
  dateigroesse: number;
}

/**
 * Ergebnis der Verfügbarkeitsprüfung für einen Rechnungsentwurf.
 *
 * `istSammelrechnung` = true bedeutet: Der Rechnungsempfänger ist Auftraggeber
 * für mindestens einen aktiven Unterkunden. In diesem Fall wird die Übernahme
 * bewusst nicht angeboten, weil das Lohnjournal fachlich beim Unterkunden liegt
 * und eine Zuordnung zum Auftraggeber nicht eindeutig wäre.
 */
export interface LohnjournalSpiegelPruefung {
  treffer: LohnjournalSpiegelTreffer | null;
  istSammelrechnung: boolean;
  fehler: string | null;
}

export interface LohnjournalSpiegelDatei {
  success: boolean;
  datei?: File;
  error?: string;
}

/**
 * Baut den Periodenschlüssel, unter dem der Spiegel Mandantendokumente ablegt.
 */
const bauePeriode = (year: number, month: number): string =>
  `${year}-${String(month).padStart(2, '0')}-01`;

export const lohnjournalSpiegelService = {

  /**
   * Prüft, ob für einen Kunden und Abrechnungsmonat ein Lohnjournal im
   * Dokumentenspiegel liegt.
   *
   * Die Prüfung ist fail-closed: Schlägt eine der beiden Abfragen fehl, wird
   * kein Treffer gemeldet, sondern ein Fehlertext zurückgegeben. Die Oberfläche
   * bietet die Übernahme dann nicht an.
   */
  pruefeVerfuegbarkeit: async (input: {
    customerId: string;
    year: number;
    month: number;
  }): Promise<LohnjournalSpiegelPruefung> => {
    try {
      // 1) Sammelrechnungs-Ausschluss: hat der Rechnungsempfänger Unterkunden?
      const { count, error: unterkundenError } = await supabase
        .from('customers')
        .select('id', { count: 'exact', head: true })
        .eq('auftraggeber_id', input.customerId)
        .eq('deleted', false);

      if (unterkundenError) {
        console.error('[LohnjournalSpiegel] Unterkunden-Prüfung fehlgeschlagen:', unterkundenError);
        return {
          treffer: null,
          istSammelrechnung: false,
          fehler: 'Es konnte nicht geprüft werden, ob es sich um eine Sammelrechnung handelt.'
        };
      }

      if ((count || 0) > 0) {
        return { treffer: null, istSammelrechnung: true, fehler: null };
      }

      // 2) Lohnjournal auf Mandantenebene suchen (employee_number IS NULL)
      const { data, error } = await supabase
        .from('datev_documents')
        .select('id, customer_id, storage_path, period, datev_timestamp, replaced_at, file_size')
        .eq('customer_id', input.customerId)
        .eq('period', bauePeriode(input.year, input.month))
        .eq('document_type', DOKUMENTTYP)
        .is('employee_number', null)
        .maybeSingle();

      if (error) {
        console.error('[LohnjournalSpiegel] Abfrage des Dokumentenspiegels fehlgeschlagen:', error);
        return {
          treffer: null,
          istSammelrechnung: false,
          fehler: 'Der Dokumentenspiegel konnte nicht abgefragt werden.'
        };
      }

      if (!data) {
        return { treffer: null, istSammelrechnung: false, fehler: null };
      }

      return {
        treffer: {
          id: data.id,
          customerId: data.customer_id,
          storagePath: data.storage_path,
          periode: data.period,
          datevZeitstempel: data.datev_timestamp,
          ersetztAm: data.replaced_at,
          dateigroesse: Number(data.file_size)
        },
        istSammelrechnung: false,
        fehler: null
      };

    } catch (error) {
      console.error('[LohnjournalSpiegel] Unerwarteter Fehler bei der Verfügbarkeitsprüfung:', error);
      return {
        treffer: null,
        istSammelrechnung: false,
        fehler: error instanceof Error ? error.message : 'Unbekannter Fehler'
      };
    }
  },

  /**
   * Lädt das gespiegelte Lohnjournal aus dem Storage und liefert es als File,
   * damit es durch dieselbe Analysekette laufen kann wie ein hochgeladenes PDF.
   *
   * Der Abruf läuft bewusst über einen frisch signierten Link mit
   * `cache: 'no-store'` statt über `supabase.storage.download()`. Damit ist
   * ausgeschlossen, dass der HTTP-Cache des Browsers eine zwischenzeitlich in
   * DATEV ersetzte Fassung überdeckt.
   */
  ladeLohnjournalDatei: async (
    treffer: LohnjournalSpiegelTreffer
  ): Promise<LohnjournalSpiegelDatei> => {
    try {
      const { data: signed, error: signError } = await supabase.storage
        .from(BUCKET)
        .createSignedUrl(treffer.storagePath, SIGNED_URL_GUELTIGKEIT_SEKUNDEN);

      if (signError || !signed || !signed.signedUrl) {
        console.error('[LohnjournalSpiegel] Signierter Link konnte nicht erstellt werden:', signError);
        return {
          success: false,
          error: 'Das Lohnjournal konnte nicht aus dem Dokumentenspiegel geladen werden.'
        };
      }

      const antwort = await fetch(signed.signedUrl, { cache: 'no-store' });

      if (!antwort.ok) {
        console.error('[LohnjournalSpiegel] Download fehlgeschlagen, HTTP', antwort.status);
        return {
          success: false,
          error: 'Das Lohnjournal konnte nicht aus dem Dokumentenspiegel geladen werden.'
        };
      }

      const blob = await antwort.blob();

      const [jahr, monat] = treffer.periode.split('-');
      const dateiname = `LOJO_${jahr}-${monat}_Dokumentenspiegel.pdf`;

      return {
        success: true,
        datei: new File([blob], dateiname, { type: 'application/pdf' })
      };

    } catch (error) {
      console.error('[LohnjournalSpiegel] Unerwarteter Fehler beim Laden der Datei:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler'
      };
    }
  }
};
